let sliderEngineStartQueue = [];
let sliderEngineStartRaf = null;
let activeWheelEngine = null;
let activeWheelTs = 0;

function scheduleSliderEngineStart(engine) {
  sliderEngineStartQueue.push(engine);
  if (sliderEngineStartRaf !== null) return;
  sliderEngineStartRaf = requestAnimationFrame(() => {
    sliderEngineStartRaf = null;
    const queued = sliderEngineStartQueue;
    sliderEngineStartQueue = [];
    queued.forEach(engine => {
      if (!engine._destroyed) engine._startLoop();
    });
  });
}

class SliderEngine {
  constructor(track, slides, opts) {
    this.track = track;
    this.opts = Object.assign({ infinite: true, slidesToScroll: 1, paged: false, disablePointerDrag: false }, opts || {});
    this.realSlides = Array.from(slides);
    this.total = this.realSlides.length;

    this.location = 0;
    this.previousLocation = 0;
    this.target = 0;
    this.offsetLocation = 0;
    this.velocity = 0;
    this.duration = 25;
    this.friction = 0.68;

    this.pageIndex = 0;
    this.snaps = [];
    this.canScroll = true;
    this.loopPoints = [];
    this._canLoop = false;
    this._slideShift = new Array(this.total).fill(0);
    this._limitMin = 0;
    this._limitMax = 0;

    this.pointerDown = false;
    this.dragAxisLocked = null;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.trackerStartX = 0;
    this.trackerStartTime = 0;
    this.trackerLastX = 0;
    this.trackerLastTime = 0;
    this.preventClick = false;
    this.preventClickTimeout = null;
    this.dragController = null;
    this.clickController = null;

    this.wheelAccX = 0;
    this.wheelAccY = 0;
    this.wheelGestureTs = 0;
    this.wheelDebounce = null;
    this.wheelAxisLock = null;

    this.interactingTimeout = null;

    this.rafId = null;
    this.animating = false;
    this.last = null;
    this.acc = 0;

    this.controller = opts && opts.controller ? opts.controller : null;
    this.onIndexChange = opts && opts.onIndexChange ? opts.onIndexChange : null;

    this.locked = !!(opts && opts.locked);
    this.viewport = this.track.parentElement || this.track;
    this.inputsAttached = false;
    this._destroyed = false;
    this._readGap();
    this._measure();
    if (!this.locked) this.attachInputs();
    this._visibilityHandler = () => { this.last = null; this.acc = 0; };
    document.addEventListener('visibilitychange', this._visibilityHandler);
    scheduleSliderEngineStart(this);
  }

  _readGap() {
    this.gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
  }

  _measure() {
    const viewportEl = this.track.parentElement || this.track;
    this.viewSize = viewportEl.getBoundingClientRect().width;
    this._computeSlideMetrics();
    this._canLoop = false;
    this._computeSnaps();
    if (this.opts.infinite && this.canScroll) {
      this._buildSlideLooper();
      this._computeSnaps();
      if (!this._canLoop) {
        this._resetSlideTransforms();
      }
    }
  }

  _computeSlideMetrics() {
    if (!this.realSlides[0]) {
      this.slideW = 0;
      this.contentSizeReal = 0;
      this.slideSizes = [];
      this.slideSizesWithGaps = [];
      this.slidePositions = [];
      this.contentSize = 0;
      return;
    }
    const columnGap = this.gap;
    this.slideSizes = this.realSlides.map(el => el.getBoundingClientRect().width);
    this.slideSizesWithGaps = this.slideSizes.map(w => w + columnGap);
    this.slideW = this.slideSizesWithGaps[0];
    let acc = 0;
    this.slidePositions = this.slideSizesWithGaps.map(step => {
      const p = -acc;
      acc += step;
      return p;
    });
    this.contentSize = acc;
    // contentSize is the loop period (N gaps, including the one separating the
    // last slide from the first clone). contentSizeReal is the real extent of
    // the content (N-1 gaps), used for the bounded-scroll limit and canScroll.
    this.contentSizeReal = this.realSlides.length > 1 ? acc - columnGap : acc;
  }

  _buildSlideLooper() {
    const ROUND = 0.5;
    const asc = this.realSlides.map((_, i) => i);
    const desc = asc.slice().reverse();
    const remGap = (indexes, from) => indexes.reduce((g, i) => g - this.slideSizesWithGaps[i], from);
    const fit = (indexes, gap) => indexes.reduce((a, i) => remGap(a, gap) > 0 ? a.concat(i) : a, []);
    const bounds = (offset) => this.slidePositions.map((snap, i) => ({
      start: snap - this.slideSizes[i] + ROUND + offset,
      end: snap + this.viewSize - ROUND + offset,
    }));
    const points = (indexes, offset, isEnd) => {
      const b = bounds(offset);
      return indexes.map((index) => {
        const initial = isEnd ? 0 : -this.contentSize;
        const altered = isEnd ? this.contentSize : 0;
        const loopPoint = isEnd ? b[index].end : b[index].start;
        return {
          index,
          loopPoint,
          prevShift: null,
          target: () => this.offsetLocation > loopPoint ? initial : altered,
        };
      });
    };
    const startGap = this.snaps[0];
    const endGap = this.viewSize - this.snaps[0] - 1;
    const startPts = points(fit(desc, startGap), this.contentSize, false);
    const endPts = points(fit(asc, endGap), -this.contentSize, true);
    this.loopPoints = startPts.concat(endPts);
    // Upstream Embla uses 0.1 here; diverged to 1 on purpose. Slides can carry
    // a fractional padding-inline derived from a viewport-relative calc (e.g.
    // announcement-bar's arrow-gutter, (100% - container-width) / 2), so their
    // widths never land on an exact integer and 0.1 flipped _canLoop to false
    // at some viewport widths, silently disabling the loop. Don't "restore" 0.1.
    this._canLoop = this.loopPoints.every(({ index }) =>
      remGap(asc.filter(i => i !== index), this.viewSize) <= 1
    );
  }

  _slideLooperLoop() {
    for (const lp of this.loopPoints) {
      const shift = lp.target();
      if (shift === lp.prevShift) continue;
      this._setSlideTransform(lp.index, shift);
      lp.prevShift = shift;
    }
  }

  _setSlideTransform(i, x) {
    if (this._slideShift[i] === x) return;
    this.realSlides[i].style.transform = x === 0 ? '' : 'translate3d(' + x + 'px,0,0)';
    this._slideShift[i] = x;
  }

  _resetSlideTransforms() {
    for (let i = 0; i < this.total; i++) {
      this._setSlideTransform(i, 0);
    }
    this.loopPoints = [];
  }

  _resyncSlideLooper() {
    for (let i = 0; i < this.total; i++) {
      this.realSlides[i].style.transform = '';
      this._slideShift[i] = 0;
    }
    for (const lp of this.loopPoints) lp.prevShift = null;
    this._slideLooperLoop();
  }

  _scrollLooperLoop(direction) {
    const JOINT = 0.1;
    const min = this._limitMin + JOINT;
    const max = this._limitMax + JOINT;
    const should = direction === 1 ? this.offsetLocation > max
      : direction === -1 ? this.offsetLocation < min : false;
    if (!should) return;
    const d = this.contentSize * (direction * -1);
    this.location += d;
    this.previousLocation += d;
    this.target += d;
    this.offsetLocation += d;
  }

  _computeSnaps() {
    const { slidesToScroll, infinite } = this.opts;
    const cols = slidesToScroll || 1;
    const rawSnaps = [];
    for (let i = 0; i < this.total; i += cols) {
      rawSnaps.push(this.slidePositions[i] !== undefined ? this.slidePositions[i] : -(i * this.slideW));
    }
    const visibleColumns = parseInt(getComputedStyle(this.track).getPropertyValue('--column-count')) || 1;
    const fitsInView = this.total <= visibleColumns;
    if (infinite && this._canLoop) {
      const tol = 1;
      this.snaps = rawSnaps;
      this.canScroll = !fitsInView && this.total > 1 && this.contentSizeReal > this.viewSize + tol;
      if (this.canScroll) {
        this._limitMax = 0;
        this._limitMin = -this.contentSize;
      }
    } else {
      const result = this._containScroll(rawSnaps);
      this.snaps = result.snaps;
      this.canScroll = !fitsInView && result.canScroll;
    }
  }

  _trackInsets() {
    const cs = getComputedStyle(this.track);
    return {
      left: parseFloat(cs.paddingLeft) || 0,
      right: parseFloat(cs.paddingRight) || 0,
    };
  }

  _containScroll(snapsAligned) {
    const { left: paddingLeft, right: paddingRight } = this._trackInsets();
    const effectiveView = this.viewSize - paddingLeft - paddingRight;
    const min = -(this.contentSizeReal - effectiveView);
    const max = 0;
    const tol = 1;

    if (this.contentSizeReal <= effectiveView + tol) {
      return { snaps: [max], canScroll: false };
    }

    const clampTo = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
    const bounded = snapsAligned.map((s, i) => {
      const c = clampTo(s, min, max);
      if (i === 0) return max;
      if (i === snapsAligned.length - 1) return min;
      if (Math.abs(min - c) <= tol) return min;
      if (Math.abs(max - c) <= tol) return max;
      return c;
    }).map(v => +v.toFixed(3));

    const lo = bounded.lastIndexOf(bounded[0]);
    const hi = bounded.indexOf(bounded[bounded.length - 1]) + 1;
    const trimmed = bounded.slice(lo, hi);
    return { snaps: trimmed, canScroll: trimmed.length > 1 };
  }

  _seek() {
    const displacement = this.target - this.location;
    if (!this.duration) {
      this.velocity = 0;
      this.previousLocation = this.location = this.target;
    } else {
      this.previousLocation = this.location;
      this.velocity += displacement / this.duration;
      this.velocity *= this.friction;
      this.location += this.velocity;
    }
  }

  _settled() {
    return Math.abs(this.target - this.offsetLocation) < 0.001;
  }

  _update() {
    this._seek();
    if (!(this.opts.infinite && this._canLoop)) {
      this._applyBounds();
    }
  }

  _applyBounds() {
    const { left: paddingLeft, right: paddingRight } = this._trackInsets();
    const effectiveView = this.viewSize - paddingLeft - paddingRight;
    const min = -(this.contentSizeReal - effectiveView);
    const max = 0;
    const clampTo = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
    const past = v => v < min || v > max;
    if (!past(this.target) && !past(this.location)) return;
    const edge = this.location < min ? min : max;
    const diffToEdge = Math.abs(edge - this.location);
    const displacement = this.target - this.location;
    const friction = clampTo(diffToEdge / (this.viewSize * 0.5), 0.1, 0.99);
    this.target -= displacement * friction;
    if (!this.pointerDown && Math.abs(displacement) < this.viewSize * 0.1) {
      this.target = clampTo(this.target, min, max);
    }
  }

  _render(alpha) {
    this.offsetLocation = this.location * alpha + this.previousLocation * (1 - alpha);
    if (this.opts.infinite && this._canLoop) {
      const direction = Math.sign(this.location - this.previousLocation) || 0;
      this._scrollLooperLoop(direction);
      this._slideLooperLoop();
    }
    this.track.style.transform = 'translate3d(' + this.offsetLocation + 'px,0,0)';
    if (this._settled() && !this.pointerDown) {
      this._stopLoop();
      this.animating = false;
    }
  }

  _frame(ts) {
    if (!this.rafId) return;
    const FIXED = 1000 / 60;
    const MAX_STEPS = 5;
    if (this.last === null) {
      this.last = ts;
      this._update();
      this._update();
    }
    this.acc += ts - this.last;
    this.last = ts;
    this.acc = Math.min(this.acc, FIXED * MAX_STEPS);
    let steps = 0;
    while (this.acc >= FIXED && steps < MAX_STEPS) {
      this._update();
      this.acc -= FIXED;
      steps++;
    }
    const alpha = this.acc / FIXED;
    this._render(alpha);
    if (this.rafId) this.rafId = requestAnimationFrame(this._frameBound);
  }

  _startLoop() {
    if (this.rafId) return;
    this._frameBound = this._frame.bind(this);
    this.last = null;
    this.acc = 0;
    this.rafId = requestAnimationFrame(this._frameBound);
  }

  _stopLoop() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  _closestSnapIndex(point) {
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < this.snaps.length; i++) {
      const d = Math.abs(this.snaps[i] - point);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return best;
  }

  _displayIndex(rawIndex) {
    const n = this.snaps.length;
    if (!n) return 0;
    return ((rawIndex % n) + n) % n;
  }

  _pageIndexFromTarget() {
    const step = (this.opts.slidesToScroll || 1) * this.slideW;
    const n = this.snaps.length;
    if (!n) return 0;
    const rawIndex = Math.round(this.target / step);
    return ((-rawIndex % n) + n) % n;
  }

  _allowedForce(rawForce, inputType) {
    const clampTo = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
    const goToNextThreshold = clampTo(this.viewSize * 0.2, 50, 225);
    if (this.opts.infinite && this._canLoop) {
      const step = (this.opts.slidesToScroll || 1) * this.slideW;
      if (Math.abs(rawForce) < goToNextThreshold) {
        const projected = this.target + rawForce;
        const nearest = Math.round(projected / step);
        const snapTarget = nearest * step;
        return snapTarget - this.target;
      }
      const maxSteps = inputType === 'touch' ? 1 : 2;
      const stepsToTake = Math.min(Math.max(Math.round(Math.abs(rawForce) / step), 1), maxSteps);
      const currentIndex = Math.round(this.target / step);
      const targetIndex = currentIndex + Math.sign(rawForce) * stepsToTake;
      return targetIndex * step - this.target;
    }
    if (Math.abs(rawForce) < goToNextThreshold) {
      const projected = this.target + rawForce;
      const i = this._closestSnapIndex(projected);
      const snap = this.snaps[i];
      return rawForce + (snap - projected);
    }
    const dir = Math.sign(rawForce);
    const nextPage = Math.max(0, Math.min(this.pageIndex - dir, this.snaps.length - 1));
    return this.snaps[nextPage] - this.target;
  }

  _clampPagedTarget(target) {
    const step = (this.opts.slidesToScroll || 1) * this.slideW;
    const start = this._pagedStartTarget ?? target;
    const rawMin = start - step;
    const rawMax = start + step;
    const clamped = Math.min(Math.max(target, rawMin), rawMax);
    return Math.round(clamped / step) * step;
  }

  _snapFromForce(rawForce, inputType) {
    if (!this.canScroll) return;
    const boost = inputType === 'touch' ? 400 : 300;
    const boosted = rawForce * boost;
    const force = this._allowedForce(boosted, inputType);
    const used = Math.abs(force - boosted);
    const base = Math.abs(boosted) || 1;
    const forceFactor = Math.min(used / base, 1);
    this.duration = 25 - 10 * forceFactor;
    this.friction = 0.68 + forceFactor / 50;
    this.target += force;
    if (this.opts.paged) this.target = this._clampPagedTarget(this.target);
    this.animating = true;
    this._startLoop();
    if (this.opts.infinite && this._canLoop) {
      const newPage = this._pageIndexFromTarget();
      if (newPage !== this.pageIndex) {
        this.pageIndex = newPage;
        this.onIndexChange && this.onIndexChange(this.pageIndex, this.snaps.length);
      }
    } else {
      const newPage = this._closestSnapIndex(this.target);
      if (newPage !== this.pageIndex) {
        this.pageIndex = newPage;
        this.onIndexChange && this.onIndexChange(this.pageIndex, this.snaps.length);
      }
    }
  }

  _snapToClosest() {
    if (!this.canScroll) return;
    if (this.opts.infinite && this._canLoop) {
      const step = (this.opts.slidesToScroll || 1) * this.slideW;
      const nearestIndex = Math.round(this.location / step);
      let snapTarget = nearestIndex * step;
      if (this.opts.paged) snapTarget = this._clampPagedTarget(snapTarget);
      this.duration = 25;
      this.friction = 0.68;
      this.target = snapTarget;
      this.animating = true;
      this._startLoop();
      this.pageIndex = this._pageIndexFromTarget();
      this.onIndexChange && this.onIndexChange(this.pageIndex, this.snaps.length);
    } else {
      const i = this._closestSnapIndex(this.location);
      this._goToPage(i, true);
    }
  }

  _goToPage(pageIdx, animate) {
    if (!this.canScroll) return;
    pageIdx = Math.max(0, Math.min(pageIdx, this.snaps.length - 1));
    this.pageIndex = pageIdx;
    const snapPos = this.snaps[pageIdx];
    if (animate === false) {
      this.location = this.previousLocation = this.target = this.offsetLocation = snapPos;
      this.velocity = 0;
      this._render(1);
      this.animating = false;
      this._startLoop();
    } else {
      this.duration = 25;
      this.friction = 0.68;
      this.target = snapPos;
      this.animating = true;
      this._startLoop();
    }
    this.onIndexChange && this.onIndexChange(this.pageIndex, this.snaps.length);
  }

  goTo(pageIdx, animate) {
    if (this.locked) return;
    if (this.opts.infinite && this._canLoop) {
      const n = this.snaps.length;
      if (!n) return;
      const target = ((pageIdx % n) + n) % n;
      const current = this.pageIndex;
      let delta = target - current;
      if (delta > n / 2) delta -= n;
      if (delta < -n / 2) delta += n;
      const step = (this.opts.slidesToScroll || 1) * this.slideW;
      if (animate === false) {
        this.target -= delta * step;
        if (this.contentSize > 0) {
          const range = this.contentSize;
          let norm = this.target % range;
          if (norm > 0) norm -= range;
          this.target = norm;
        }
        this.location = this.previousLocation = this.offsetLocation = this.target;
        this.velocity = 0;
        this._resyncSlideLooper();
        this.track.style.transform = 'translate3d(' + this.offsetLocation + 'px,0,0)';
        this.animating = false;
        this._startLoop();
      } else {
        this.duration = 25;
        this.friction = 0.68;
        this.target -= delta * step;
        this.animating = true;
        this._startLoop();
      }
      this.pageIndex = target;
      this.onIndexChange && this.onIndexChange(this.pageIndex, n);
    } else {
      this._goToPage(pageIdx, animate);
    }
  }

  next() {
    if (this.locked) return;
    if (this.opts.infinite && this._canLoop) {
      const step = (this.opts.slidesToScroll || 1) * this.slideW;
      this.duration = 25;
      this.friction = 0.68;
      this.target -= step;
      this.animating = true;
      this._startLoop();
      this.pageIndex = this._displayIndex(this.pageIndex + 1);
      this.onIndexChange && this.onIndexChange(this.pageIndex, this.snaps.length);
    } else {
      this._goToPage(Math.min(this.pageIndex + 1, this.snaps.length - 1), true);
    }
  }

  prev() {
    if (this.locked) return;
    if (this.opts.infinite && this._canLoop) {
      const step = (this.opts.slidesToScroll || 1) * this.slideW;
      this.duration = 25;
      this.friction = 0.68;
      this.target += step;
      this.animating = true;
      this._startLoop();
      this.pageIndex = this._displayIndex(this.pageIndex - 1);
      this.onIndexChange && this.onIndexChange(this.pageIndex, this.snaps.length);
    } else {
      this._goToPage(Math.max(this.pageIndex - 1, 0), true);
    }
  }

  reanchor() {
    this._reposition();
    if (this.onIndexChange) this.onIndexChange(this.pageIndex, this.snaps.length);
  }

  // Re-measures and snaps the track back to the current logical slide, without
  // firing onIndexChange (no navigation happened — the slide is the same,
  // only its pixel position needs to track the current width). Split out of
  // reanchor() so a resize-in-progress can call this every frame (cheap,
  // silent) while reanchor() itself still fires the index-change callback
  // once, after the heavier resize handling settles.
  _reposition() {
    if (this.locked) return;
    // Capture the logical slide before _measure() recomputes slideW/snaps from
    // the (possibly mid-resize) current widths, so a changed slideW can't drift
    // the restored position into a different slide.
    const requestedIndex = this.pageIndex;
    this._readGap();
    this._measure();
    const i = Math.max(0, Math.min(requestedIndex, this.snaps.length - 1));
    this.pageIndex = i;
    const pos = this.snaps[i] ?? 0;
    // Set the position directly instead of delegating to _goToPage(), which
    // no-ops entirely when !this.canScroll — that silently left the track's
    // transform (and therefore the visible slide) stuck at its pre-resize
    // value whenever a resize also flipped canScroll.
    this.location = this.previousLocation = this.target = this.offsetLocation = pos;
    this.velocity = 0;
    if (this.opts.infinite && this._canLoop) {
      this._resyncSlideLooper();
    }
    this._render(1);
    this.animating = false;
    this._startLoop();
  }

  _repositionFast(ratio) {
    if (this.locked || !this.total) return;
    if (!ratio || !isFinite(ratio) || ratio <= 0 || ratio === 1) return;
    this.viewSize *= ratio;
    this.slideSizes = this.slideSizes.map(w => w * ratio);
    this.slideSizesWithGaps = this.slideSizesWithGaps.map(w => w * ratio);
    this.slidePositions = this.slidePositions.map(p => p * ratio);
    this.slideW = this.slideSizesWithGaps[0] || 0;
    this.contentSize *= ratio;
    this.contentSizeReal *= ratio;
    this.snaps = this.snaps.map(s => s * ratio);
    this._limitMin *= ratio;
    this._limitMax *= ratio;
    this.location *= ratio;
    this.previousLocation *= ratio;
    this.target *= ratio;
    this.offsetLocation *= ratio;
    if (this.loopPoints.length) {
      this.loopPoints.forEach(lp => { lp.loopPoint *= ratio; });
    }
    if (this.opts.infinite && this._canLoop) {
      for (let i = 0; i < this.total; i++) {
        if (this._slideShift[i] === 0) continue;
        this._slideShift[i] *= ratio;
        this.realSlides[i].style.transform = 'translate3d(' + this._slideShift[i] + 'px,0,0)';
      }
    }
    this.track.style.transform = 'translate3d(' + this.offsetLocation + 'px,0,0)';
  }

  _readPoint(e) {
    if (e.touches && e.touches[0]) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  }

  _isNativeZoomed() {
    return !!(window.visualViewport && window.visualViewport.scale > 1.01);
  }

  _onPointerDown(e, isTouch) {
    if (!this.canScroll) return;
    this.pointerDown = true;
    this.dragAxisLocked = null;
    this.preventClick = false;
    clearTimeout(this.preventClickTimeout);
    this.clickController?.abort();
    this.clickController = new AbortController();
    document.addEventListener('click', this._boundClickCapture, { signal: this.clickController.signal, capture: true });
    const pt = this._readPoint(e);
    this.dragStartX = pt.x;
    this.dragStartY = pt.y;
    this.trackerStartX = pt.x;
    this.trackerStartTime = performance.now();
    this.trackerLastX = pt.x;
    this.trackerLastTime = performance.now();
    this.duration = 0;
    this.friction = 0;
    this.target = this.location;
    this._pagedStartTarget = this.target;
    this._startLoop();
    this.viewport.classList.add('grabbing');
  }

  _onPointerMove(e, isTouch) {
    if (!this.pointerDown) return;

    if (isTouch && e.touches && e.touches.length > 1) {
      this._onPointerUp(e, isTouch);
      return;
    }

    if (this.dragAxisLocked === 'x' && !e.cancelable) {
      this._onPointerUp(e, isTouch);
      return;
    }

    const pt = this._readPoint(e);
    const dx = pt.x - this.dragStartX;
    const dy = pt.y - this.dragStartY;

    if (this.dragAxisLocked === null) {
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        this.dragAxisLocked = isTouch
          ? (Math.abs(dy) > Math.abs(dx) ? 'y' : 'x')
          : (Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y');
        if (this.dragAxisLocked === 'y') {
          this.pointerDown = false;
          this.viewport.classList.remove('grabbing');
          this.track.classList.remove('moving');
          return;
        }
      }
    }

    if (this.dragAxisLocked !== 'x') return;

    e.stopPropagation();
    if (e.cancelable) e.preventDefault();

    const now = performance.now();
    const timeSinceStart = now - this.trackerStartTime;
    if (timeSinceStart > 170) {
      this.trackerStartX = this.trackerLastX;
      this.trackerStartTime = this.trackerLastTime;
    }
    this.trackerLastX = pt.x;
    this.trackerLastTime = now;

    const diff = pt.x - this._prevPt;
    this._prevPt = pt.x;
    this.duration = 0.75;
    this.friction = 0.3;
    this.target += diff;
    this.track.classList.add('moving');

    if (Math.abs(pt.x - this.dragStartX) > 10) this.preventClick = true;
  }

  _onPointerUp(e, isTouch) {
    if (!this.pointerDown) return;
    this.pointerDown = false;
    this.dragController?.abort();
    this.dragController = null;
    this.viewport.classList.remove('grabbing');
    this.track.classList.remove('moving');

    clearTimeout(this.preventClickTimeout);
    this.preventClickTimeout = setTimeout(() => {
      this.clickController?.abort();
      this.clickController = null;
    }, 0);

    if (this.dragAxisLocked !== 'x') {
      this.duration = 25;
      this.friction = 0.68;
      this._snapToClosest();
      return;
    }

    const now = performance.now();
    const dt = now - this.trackerStartTime;
    let rawForce = 0;
    const isFlick = dt > 0 && (now - this.trackerLastTime <= 170);
    if (isFlick) {
      const traveled = this.trackerLastX - this.trackerStartX;
      rawForce = traveled / dt;
    }

    if (isFlick && Math.abs(rawForce) > 0.1) {
      this._snapFromForce(rawForce, isTouch ? 'touch' : 'mouse');
    } else {
      this.duration = 25;
      this.friction = 0.68;
      this._snapToClosest();
    }
  }

  _onMouseDown(e) {
    if (e.button !== 0) return;
    if (e.target.closest('.slider__controls, .media-zoom')) return;
    if (this.canScroll) e.stopPropagation();
    e.preventDefault();
    this._prevPt = e.clientX;
    this._onPointerDown(e, false);
    this.dragController = new AbortController();
    const sig = this.dragController.signal;
    document.addEventListener('mousemove', this._boundMouseMove, { signal: sig });
    document.addEventListener('mouseup', this._boundMouseUp, { signal: sig });
    document.addEventListener('mouseleave', this._boundMouseUp, { signal: sig });
  }

  _onMouseMove(e) {
    this._onPointerMove(e, false);
  }

  _onMouseUp(e) {
    this._onPointerUp(e, false);
  }

  _onClickCapture(e) {
    if (this.preventClick) {
      e.stopPropagation();
      e.preventDefault();
      this.preventClick = false;
    }
  }

  _onTouchStart(e) {
    if (e.touches.length > 1 || this._isNativeZoomed()) return;
    if (e.target.closest('.slider__controls, .media-zoom')) return;
    if (this.canScroll) e.stopPropagation();
    this._prevPt = e.touches[0].clientX;
    this._onPointerDown(e, true);
    clearTimeout(this.interactingTimeout);
    this.viewport.setAttribute('interacting', '');
  }

  _onTouchMove(e) {
    this._onPointerMove(e, true);
  }

  _onTouchEnd(e) {
    this._onPointerUp(e, true);
    clearTimeout(this.interactingTimeout);
    this.interactingTimeout = setTimeout(() => {
      this.viewport.removeAttribute('interacting');
    }, 2000);
  }

  _onWheel(e) {
    if (!this.canScroll) return;
    if (e.target.closest('.slider__controls')) return;

    const now = performance.now();

    if (activeWheelEngine && (activeWheelEngine._destroyed || now - activeWheelTs > 150)) {
      activeWheelEngine = null;
    }
    // Cross-instance wheel-gesture ownership: the browser delivers wheel
    // ticks to whatever element is under the cursor on that tick, not to the
    // one that received the gesture's first tick. If a later tick of an
    // in-progress horizontal gesture lands on a different theme-slider (e.g.
    // the page scrolls a nested product-card gallery under the cursor
    // mid-gesture), that instance would otherwise see a "fresh" wheelAxisLock
    // and steal the gesture. Forward the delta to the instance that already
    // claimed it instead.
    if (activeWheelEngine && activeWheelEngine !== this) {
      activeWheelTs = now;
      e.preventDefault();
      e.stopPropagation();
      activeWheelEngine._applyWheelDelta(e);
      return;
    }

    if (this.wheelAxisLock !== null && now - this.wheelGestureTs > 150) {
      this.wheelAxisLock = null;
      this.wheelAccX = 0;
      this.wheelAccY = 0;
    }
    this.wheelGestureTs = now;

    if (this.wheelAxisLock === 'y') return;

    if (this.wheelAxisLock === null) {
      this.wheelAccX += e.deltaX;
      this.wheelAccY += e.deltaY;
      const absX = Math.abs(this.wheelAccX);
      const absY = Math.abs(this.wheelAccY);
      if (absX < 2 && absY < 2) {
        e.preventDefault();
        return;
      }
      if (absY >= absX) {
        this.wheelAxisLock = 'y';
        return;
      }
      this.wheelAxisLock = 'x';
      activeWheelEngine = this;
    }

    activeWheelTs = now;

    // Committed to horizontal handling for this gesture: consume the event here
    // so a scrollable nested slider (e.g. the product-card gallery inside a
    // product carousel) doesn't also bubble the same wheel tick up to an
    // ancestor slider's own wheel listener and move both at once. The vertical
    // branch above intentionally returns without stopping propagation, so the
    // page (and any ancestor slider's own axis detection) still sees it.
    e.stopPropagation();
    e.preventDefault();

    this._applyWheelDelta(e);
  }

  _applyWheelDelta(e) {
    this.wheelGestureTs = performance.now();

    let delta = e.deltaX;
    if (e.deltaMode === 1) delta *= 16;
    else if (e.deltaMode === 2) delta *= 100;

    this.duration = 0;
    this.friction = 0;
    this.target -= delta;
    if (this.opts.infinite && this._canLoop) {
      const sz = this.contentSize;
      if (this.target > this._limitMax) {
        const shift = -sz;
        this.target += shift;
        this.location += shift;
        this.previousLocation += shift;
      } else if (this.target < this._limitMin) {
        const shift = sz;
        this.target += shift;
        this.location += shift;
        this.previousLocation += shift;
      }
    } else {
      const { left: paddingLeft, right: paddingRight } = this._trackInsets();
      const min = -(this.contentSizeReal - (this.viewSize - paddingLeft - paddingRight));
      const max = 0;
      const clampTo = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
      this.target = clampTo(this.target, min, max);
    }
    this._startLoop();

    clearTimeout(this.wheelDebounce);
    this.wheelDebounce = setTimeout(() => {
      this.wheelAxisLock = null;
      this.wheelAccX = 0;
      this.wheelAccY = 0;
      this.duration = 25;
      this.friction = 0.68;
      this._snapToClosest();
      if (activeWheelEngine === this) activeWheelEngine = null;
    }, 120);
  }

  attachInputs() {
    if (this.inputsAttached) return;

    this._boundMouseMove = this._boundMouseMove || this._onMouseMove.bind(this);
    this._boundMouseUp = this._boundMouseUp || this._onMouseUp.bind(this);
    this._boundClickCapture = this._boundClickCapture || this._onClickCapture.bind(this);
    this._boundMouseDown = this._boundMouseDown || this._onMouseDown.bind(this);
    this._boundTouchStart = this._boundTouchStart || this._onTouchStart.bind(this);
    this._boundTouchMove = this._boundTouchMove || this._onTouchMove.bind(this);
    this._boundTouchEnd = this._boundTouchEnd || this._onTouchEnd.bind(this);
    this._boundWheel = this._boundWheel || this._onWheel.bind(this);

    this.inputController = new AbortController();
    const sig = this.inputController.signal;

    // Opt-in, additive: when disablePointerDrag is set and the device reports
    // a fine pointer with hover support (mouse/trackpad, not a touchscreen),
    // skip wiring mouse-drag and wheel/trackpad-swipe navigation entirely, so
    // the slider only advances via its prev/next controls on desktop. Touch
    // input is never gated by this flag — every consumer that omits it is
    // completely unaffected.
    const skipPointerDrag = this.opts.disablePointerDrag && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (!skipPointerDrag) {
      this.viewport.addEventListener('mousedown', this._boundMouseDown, { signal: sig });
      this.viewport.addEventListener('wheel', this._boundWheel, { passive: false, signal: sig });
    }
    this.viewport.addEventListener('touchstart', this._boundTouchStart, { passive: true, signal: sig });
    this.viewport.addEventListener('touchmove', this._boundTouchMove, { passive: false, signal: sig });
    this.viewport.addEventListener('touchend', this._boundTouchEnd, { signal: sig });
    this.viewport.addEventListener('touchcancel', this._boundTouchEnd, { signal: sig });
    this.inputsAttached = true;
  }

  detachInputs() {
    if (!this.inputsAttached) return;
    this.inputController?.abort();
    this.inputController = null;
    this.inputsAttached = false;
    if (this.pointerDown) {
      this.pointerDown = false;
      this.dragController?.abort();
      this.dragController = null;
      clearTimeout(this.preventClickTimeout);
      this.clickController?.abort();
      this.clickController = null;
      this.viewport.classList.remove('grabbing');
      this.track.classList.remove('moving');
    }
  }

  lock() {
    if (this.locked) return;
    this.locked = true;
    this.detachInputs();
    this._stopLoop();
    this.location = this.previousLocation = this.target = this.offsetLocation = 0;
    this._resetSlideTransforms();
    this.track.style.transform = '';
  }

  unlock() {
    if (!this.locked) return;
    this.locked = false;
    this.pageIndex = 0;
    this.location = this.previousLocation = this.target = this.offsetLocation = 0;
    this._readGap();
    this._measure();
    this.attachInputs();
    this._startLoop();
  }

  destroy() {
    this._destroyed = true;
    this._stopLoop();
    this.detachInputs();
    this.dragController?.abort();
    this.clickController?.abort();
    clearTimeout(this.wheelDebounce);
    clearTimeout(this.interactingTimeout);
    clearTimeout(this.preventClickTimeout);
    if (activeWheelEngine === this) activeWheelEngine = null;
    document.removeEventListener('visibilitychange', this._visibilityHandler);
    this._resetSlideTransforms();
    this.track.style.transform = '';
  }
}

if (!customElements.get('theme-slider')) {
  class ThemeSlider extends HTMLElement {
    connectedCallback() {
      if (this.engine) return;
      this.initObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            this.initObserver.disconnect();
            this.initObserver = null;
            this._setup();
          }
        },
        { rootMargin: '200px' }
      );
      this.initObserver.observe(this);
    }

    disconnectedCallback() {
      this.initObserver?.disconnect();
      this.controller?.abort();
      this.engine?.destroy();
      this.engine = null;
    }

    _setup() {
      if (this.engine) return;
      this.controller = new AbortController();
      const sig = this.controller.signal;

      this.track = this.querySelector(':scope > .slider__list');
      if (!this.track) return;

      this.realSlides = Array.from(this.track.querySelectorAll(':scope > .slider__item, :scope > .shopify-block > .slider__item'));
      if (!this.realSlides.length) return;

      const controls = this.querySelector(':scope > .slider__controls');
      this.prevBtn = controls?.querySelector('.slider__button.prev') ?? null;
      this.nextBtn = controls?.querySelector('.slider__button.next') ?? null;
      this.counterEl = controls?.querySelector('.slider__counter__thumbnails') ?? null;
      this.progressEl = controls?.querySelector('.slider__counter__progress') ?? null;
      this.progressFillEl = this.progressEl?.querySelector('.slider__counter__progress__fill') ?? null;
      this.counterType = this.dataset.counter;
      this.infinite = this.dataset.infinite !== 'false';
      this.autorotateEnabled = this.hasAttribute('autorotate');
      this.autorotateSpeed = parseFloat(this.dataset.autorotateSpeed) || 5;
      this.autorotateTimer = null;
      this.autoPausedByInteraction = false;

      const slidesToScroll = this._getSlidesToScroll();

      this.gridLocked = this._isGridLockedForViewport();

      this.engine = new SliderEngine(this.track, this.realSlides, {
        infinite: this.infinite,
        slidesToScroll,
        paged: this.dataset.paged === 'true',
        controller: this.controller,
        locked: this.gridLocked,
        disablePointerDrag: this.hasAttribute('data-disable-pointer-drag'),
        onIndexChange: (pageIndex, totalPages) => this._onEnginePageChange(pageIndex, totalPages),
      });

      this._schedulePromoteAdjacentImages(sig);

      this._setupA11y();

      if (this.gridLocked || !this.engine.canScroll) {
        this._hideControls();
      } else {
        this._setupControls(sig);
        this._controlsWired = true;
        this._setupCounter();
        this._updateCounterState(0);
        this._updateArrows(0);
      }

      this._setupResize(sig);

      if (this.autorotateEnabled) {
        this._setupAutorotatePause(sig);
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        reducedMotion.addEventListener('change', (e) => {
          if (e.matches) this._stopAutorotate();
          else if (this.autorotateEnabled && !this.gridLocked && this.engine?.canScroll) this._startAutorotate();
        }, { signal: sig });
        if (!this.gridLocked && this.engine.canScroll && !reducedMotion.matches) {
          this._startAutorotate();
        }
      }

      requestAnimationFrame(() => this.classList.add('loaded'));
    }

    _getSlidesToScroll() {
      const fixed = parseInt(this.dataset.slidesToScroll);
      return fixed > 0 ? fixed : 1;
    }

    _hideControls() {
      this.setAttribute('no-scroll', '');
    }

    _showControls() {
      this.removeAttribute('no-scroll');
    }

    _isGridLockedForViewport() {
      if (window.matchMedia('(max-width: 749px)').matches) {
        return this.classList.contains('forced-grid-mobile');
      }
      if (window.matchMedia('(min-width: 750px) and (max-width: 1023px)').matches) {
        return this.classList.contains('forced-grid-tablet');
      }
      return this.classList.contains('forced-grid-desktop');
    }

    _setupA11y() {
      this.setAttribute('role', 'group');
      this.setAttribute('aria-roledescription', theme.locales?.slider?.carouselLabel || 'carousel');

      this.liveRegion = this.querySelector('[aria-live]');
      if (!this.liveRegion) {
        this.liveRegion = document.createElement('div');
        this.liveRegion.setAttribute('aria-live', 'polite');
        this.liveRegion.setAttribute('aria-atomic', 'true');
        this.liveRegion.classList.add('visually-hidden');
        this.appendChild(this.liveRegion);
      }

      this.realSlides.forEach((slide, i) => {
        if (slide.dataset.sliderOwnRole === undefined) {
          slide.dataset.sliderOwnRole = slide.getAttribute('role') && slide.getAttribute('role') !== 'group' ? 'true' : 'false';
        }
        if (slide.dataset.sliderOwnRole === 'true') return;
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', (i + 1) + ' / ' + this.realSlides.length);
      });

      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
    }

    _updateA11y(pageIndex) {
      if (this.engine?.animating) return;
      const slidesToScroll = this.engine?.opts.slidesToScroll || 1;
      const start = pageIndex * slidesToScroll;
      const end = start + slidesToScroll;

      this.realSlides.forEach((slide, i) => {
        if (slide.dataset.sliderOwnRole === 'true') return;
        const visible = i >= start && i < end;
        slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
        const focusables = slide.querySelectorAll('a,button,input,select,textarea,[tabindex]');
        focusables.forEach(el => {
          if (el.getAttribute('aria-hidden') === 'true') return;
          if (visible) {
            const saved = el.getAttribute('data-tabindex-saved');
            if (saved !== null) {
              el.setAttribute('tabindex', saved);
              el.removeAttribute('data-tabindex-saved');
            } else {
              el.removeAttribute('tabindex');
            }
          } else {
            if (!el.hasAttribute('data-tabindex-saved')) {
              const current = el.getAttribute('tabindex');
              if (current !== null) el.setAttribute('data-tabindex-saved', current);
            }
            el.setAttribute('tabindex', '-1');
          }
        });
        if (!visible && document.activeElement && slide.contains(document.activeElement)) {
          this.focus();
        }
      });

      this._announceSlide(pageIndex + 1, this.engine?.snaps.length || 1);
    }

    _announceSlide(current, total) {
      const label = theme.locales?.slider?.slideOf
        ? theme.locales.slider.slideOf.replace('[current]', current).replace('[total]', total)
        : current + ' / ' + total;
      this.setAttribute('aria-busy', 'true');
      const span = document.createElement('span');
      span.textContent = label;
      this.liveRegion.innerHTML = '';
      this.liveRegion.appendChild(span);
      setTimeout(() => {
        this.liveRegion.innerHTML = '';
        this.removeAttribute('aria-busy');
      }, 150);
    }

    _setupControls(sig) {
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.engine.next();
          this._pauseAutorotateOnInteraction();
        }, { signal: sig });
      }
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.engine.prev();
          this._pauseAutorotateOnInteraction();
        }, { signal: sig });
      }

      this.addEventListener('keydown', (e) => {
        const total = this.engine?.snaps.length || 1;
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            e.stopPropagation();
            this.engine?.prev();
            break;
          case 'ArrowRight':
            e.preventDefault();
            e.stopPropagation();
            this.engine?.next();
            break;
          case 'Home':
            e.preventDefault();
            e.stopPropagation();
            this.engine?.goTo(0);
            break;
          case 'End':
            e.preventDefault();
            e.stopPropagation();
            this.engine?.goTo(total - 1);
            break;
        }
      }, { signal: sig });
    }

    _setupCounter() {
      if (this.counterType === 'progress') {
        if (!this.progressEl) return;
        this.progressTotal = this.engine?.snaps.length || this.realSlides.length;
        this.progressEl.setAttribute('aria-valuemax', this.progressTotal);
        return;
      }

      if (!this.counterEl) return;
      if (this.counterType !== 'dots' && this.counterType !== 'numbers') return;

      const totalPages = this.engine?.snaps.length || this.realSlides.length;
      this.counterEl.innerHTML = '';
      const frag = document.createDocumentFragment();
      for (let i = 0; i < totalPages; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.classList.add('thumbnail', 'thumbnail--' + (this.counterType === 'dots' ? 'dot' : 'number'));
        btn.setAttribute('aria-label', (i + 1) + ' / ' + totalPages);
        if (this.counterType === 'numbers') btn.textContent = i + 1;
        btn.addEventListener('click', () => {
          this.engine?.goTo(i);
          this._pauseAutorotateOnInteraction();
        });
        frag.appendChild(btn);
      }
      this.counterEl.appendChild(frag);
      this.dots = Array.from(this.counterEl.querySelectorAll('.thumbnail'));
    }

    _updateCounterState(pageIndex) {
      if (this.counterType === 'progress') {
        if (!this.progressEl || !this.progressFillEl) return;
        const total = this.progressTotal || 1;
        const current = pageIndex + 1;
        this.progressFillEl.style.transform = 'scaleX(' + (current / total) + ')';
        this.progressEl.setAttribute('aria-valuenow', current);
        this.progressEl.setAttribute('aria-valuetext', current + ' / ' + total);
        return;
      }

      if (!this.dots?.length) return;
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === pageIndex);
      });
    }

    _updateArrows(pageIndex) {
      if (!this.prevBtn && !this.nextBtn) return;
      if (this.infinite && this.engine?._canLoop) {
        this.prevBtn?.removeAttribute('disabled');
        this.prevBtn?.removeAttribute('aria-disabled');
        this.prevBtn?.classList.remove('disabled');
        this.nextBtn?.removeAttribute('disabled');
        this.nextBtn?.removeAttribute('aria-disabled');
        this.nextBtn?.classList.remove('disabled');
        return;
      }

      const total = this.engine?.snaps.length || 1;
      const atStart = pageIndex === 0;
      const atEnd = pageIndex === total - 1;

      if (this.prevBtn) {
        this.prevBtn.toggleAttribute('disabled', atStart);
        this.prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
        this.prevBtn.classList.toggle('disabled', atStart);
      }
      if (this.nextBtn) {
        this.nextBtn.toggleAttribute('disabled', atEnd);
        this.nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
        this.nextBtn.classList.toggle('disabled', atEnd);
      }
    }

    _promoteAdjacentImages(pageIndex) {
      const cols = this.engine?.opts.slidesToScroll || 1;
      const total = this.realSlides.length;
      const prevIdx = ((pageIndex - 1) * cols + total) % total;
      const nextIdx = ((pageIndex + 1) * cols) % total;
      [prevIdx, nextIdx].forEach(idx => {
        const slide = this.realSlides[idx];
        if (!slide) return;
        slide.querySelectorAll('img[loading="lazy"]').forEach(img => {
          img.setAttribute('loading', 'eager');
          img.setAttribute('fetchpriority', 'low');
          if (img.src) img.decode().catch(() => {});
        });
      });
    }

    _schedulePromoteAdjacentImages(sig) {
      let done = false;
      const run = () => {
        if (done) return;
        done = true;
        this._promoteAdjacentImages(this.engine?.pageIndex ?? 0);
      };
      if (window.requestIdleCallback) {
        const idleHandle = window.requestIdleCallback(run, { timeout: 2000 });
        sig.addEventListener('abort', () => window.cancelIdleCallback(idleHandle), { once: true });
      } else {
        const timeoutHandle = setTimeout(run, 2000);
        sig.addEventListener('abort', () => clearTimeout(timeoutHandle), { once: true });
      }
      window.addEventListener('load', run, { once: true, signal: sig });
      ['pointerdown', 'touchstart', 'wheel', 'focusin'].forEach(type => {
        this.addEventListener(type, run, { once: true, passive: true, signal: sig });
      });
    }

    _onEnginePageChange(pageIndex, totalPages) {
      this._updateCounterState(pageIndex);
      this._updateArrows(pageIndex);
      this._updateA11y(pageIndex);
      this._promoteAdjacentImages(pageIndex);
      theme.pubsub?.publish?.(theme.pubsub.events.SLIDER_CHANGE, {
        id: this.id,
        index: pageIndex,
        total: totalPages,
        currentIndex: pageIndex,
        totalSlides: this.realSlides.length,
        isInfinite: this.infinite,
      });
      if (this.autorotateEnabled) this._resetAutorotate();
    }

    _setupResize(sig) {
      let resizeTimer = null;
      let lastWidth = null;
      const observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect ? entries[0].contentRect.width : this.offsetWidth;
        if (lastWidth === null) {
          lastWidth = width;
          return;
        }
        if (Math.abs(width - lastWidth) < 0.5) return;
        const ratio = width / lastWidth;
        lastWidth = width;

        // Continuous CSS width transitions (e.g. the desktop filter sidebar
        // reflow) fire this callback on every frame; re-sync the track to the
        // current slide's position at the CURRENT width immediately, on every
        // frame, so it never drifts while the debounced handler below waits
        // for the animation to settle before doing its heavier work.
        if (!this.gridLocked) this.engine?._repositionFast(ratio);

        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          if (this.offsetParent === null) return;

          const wasGridLocked = this.gridLocked;
          this.gridLocked = this._isGridLockedForViewport();

          if (this.gridLocked && !wasGridLocked) {
            this.engine?.lock();
            this._hideControls();
            if (this.autorotateEnabled) this._stopAutorotate();
            return;
          }
          if (!this.gridLocked && wasGridLocked) {
            this.engine?.unlock();
            if (this.autorotateEnabled && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
              this._startAutorotate();
            }
          }
          if (this.gridLocked) return;

          const prevSnapCount = this.engine?.snaps.length;
          if (this.engine && !this.dataset.slidesToScroll) {
            this.engine.opts.slidesToScroll = this._getSlidesToScroll();
          }
          this.engine?.reanchor();
          const nowCanScroll = this.engine?.canScroll;
          const nowSnapCount = this.engine?.snaps.length;
          const hasNoScroll = this.hasAttribute('no-scroll');
          if (nowCanScroll && hasNoScroll) {
            this._showControls();
            this._setupCounter();
            if (!this._controlsWired) {
              this._setupControls(sig);
              this._controlsWired = true;
            }
          } else if (!nowCanScroll && !hasNoScroll) {
            this._hideControls();
          } else if (nowCanScroll && nowSnapCount !== prevSnapCount) {
            this._setupCounter();
          }
          if (nowCanScroll) {
            this._updateCounterState(this.engine.pageIndex);
            this._updateArrows(this.engine.pageIndex);
          }
        }, 100);
      });
      observer.observe(this);
      sig.addEventListener('abort', () => observer.disconnect());
    }

    _startAutorotate() {
      this.setAttribute('play', '');
      this._updateAutorotateButton(true);
      this.autorotateTimer = setInterval(() => {
        this.engine?.next();
      }, this.autorotateSpeed * 1000);
    }

    _stopAutorotate() {
      this.removeAttribute('play');
      this._updateAutorotateButton(false);
      clearInterval(this.autorotateTimer);
      this.autorotateTimer = null;
    }

    _resetAutorotate() {
      if (!this.autorotateEnabled || this.autoPausedByInteraction) return;
      clearInterval(this.autorotateTimer);
      this.autorotateTimer = setInterval(() => {
        this.engine?.next();
      }, this.autorotateSpeed * 1000);
    }

    _pauseAutorotateOnInteraction() {
      if (!this.autorotateEnabled) return;
      this._stopAutorotate();
      this.autoPausedByInteraction = true;
    }

    _updateAutorotateButton(playing) {
      const btn = this.querySelector('.slider__play');
      if (!btn) return;
      btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
      btn.setAttribute('aria-label', playing
        ? theme.locales?.slider?.pauseSlideshow
        : theme.locales?.slider?.playSlideshow);
    }

    _setupAutorotatePause(sig) {
      const stopOnHover = this.dataset.stopOnHover !== 'false';

      if (stopOnHover) {
        this.addEventListener('mouseenter', () => {
          if (!this.hasAttribute('play')) return;
          this._stopAutorotate();
          this.autoPausedByInteraction = true;
        }, { signal: sig });

        this.addEventListener('mouseleave', () => {
          if (!this.autoPausedByInteraction) return;
          if (this.contains(document.activeElement)) return;
          this.autoPausedByInteraction = false;
          this._startAutorotate();
        }, { signal: sig });
      }

      this.addEventListener('focusin', () => {
        if (!this.hasAttribute('play')) return;
        this._stopAutorotate();
        this.autoPausedByInteraction = true;
      }, { signal: sig });

      this.addEventListener('focusout', (e) => {
        if (!this.autoPausedByInteraction) return;
        if (this.contains(e.relatedTarget)) return;
        if (this.matches(':hover')) return;
        this.autoPausedByInteraction = false;
        this._startAutorotate();
      }, { signal: sig });

      const playBtn = this.querySelector('.slider__play');
      if (playBtn) {
        playBtn.addEventListener('click', () => {
          if (this.hasAttribute('play')) {
            this._stopAutorotate();
            this.autoPausedByInteraction = true;
            theme.pubsub?.publish?.(theme.pubsub.events.SLIDER_AUTOROTATE_TOGGLE, { id: this.id, playing: false });
          } else {
            this.autoPausedByInteraction = false;
            this._startAutorotate();
            theme.pubsub?.publish?.(theme.pubsub.events.SLIDER_AUTOROTATE_TOGGLE, { id: this.id, playing: true });
          }
        }, { signal: sig });
      }
    }

    setUpSlider() {
      if (this.engine) return;
      this._setup();
    }

    reanchor() {
      this.engine?.reanchor();
    }

    next() {
      if (!this.engine) this._setup();
      this.engine?.next();
    }

    prev() {
      if (!this.engine) this._setup();
      this.engine?.prev();
    }

    navigateToSlide(index, animate) {
      if (!this.engine) this._setup();
      if (!this.engine) return;
      const cols = this.engine.opts.slidesToScroll || 1;
      const pageIdx = Math.floor(index / cols);
      this.engine.goTo(pageIdx, animate);
    }

    /**
     * Rebuilds the engine against the current DOM, preserving the active
     * page. Call after mutating the slide list externally — insertSlide()
     * already calls this internally.
     */
    refresh() {
      const activeIndex = this.engine?.pageIndex || 0;
      this.controller?.abort();
      this.engine?.destroy();
      this.engine = null;
      this._setup();
      if (this.engine) {
        const total = this.engine.snaps.length;
        this.engine.goTo(Math.min(activeIndex, Math.max(total - 1, 0)), false);
      }
    }

    /**
     * Inserts `element` as a slide at `index` — accounting for .shopify-block
     * wrappers — renumbers every slide's data-index, and refreshes the engine.
     */
    insertSlide(element, index) {
      if (!this.track) return;
      const items = Array.from(this.track.querySelectorAll(':scope > .slider__item, :scope > .shopify-block > .slider__item'));
      const referenceItem = items[index] || null;
      const reference = referenceItem
        ? (referenceItem.parentElement?.classList.contains('shopify-block') ? referenceItem.parentElement : referenceItem)
        : null;
      const wrapper = element.closest?.('.shopify-block') || element;
      if (reference) {
        this.track.insertBefore(wrapper, reference);
      } else {
        this.track.appendChild(wrapper);
      }
      const updated = Array.from(this.track.querySelectorAll(':scope > .slider__item, :scope > .shopify-block > .slider__item'));
      updated.forEach((slide, i) => { slide.dataset.index = i; });
      this.refresh();
    }
  }

  customElements.define('theme-slider', ThemeSlider);
}
